"use client"

import { FormikProvider, useFormik, useFormikContext } from "formik"
import { AlertCircle, Loader2 } from "lucide-react"

import { FormInput, FormSelect } from "@/components/form-inputs"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useImportKnowledgeBaseFromSocialMutation } from "@/hooks/mutations/knowledge-base-social-import"
import {
  getSocialPlatform,
  SocialProfileUrlSchema,
} from "@/lib/ai/knowledge-base/social-import/url"
import {
  getKbSocialImportMaxPosts,
  KB_SOCIAL_IMPORT_DEFAULT_POSTS,
  type KbSocialImportPlatform,
} from "@/lib/jobs/kb-social-import-shared"
import type {
  ImportKnowledgeBaseFromSocialInput,
  ImportKnowledgeBaseFromSocialResult,
} from "@/server-actions/knowledge-base-social-import"

type SocialImportFormValues = {
  profileUrl: string
  maxPosts: number
  category: string
  tags: string
  injectionMode: "auto" | "direct" | "rag"
  isDefault: boolean
}

export type SocialImportPlatform = KbSocialImportPlatform

type ImportFromSocialDialogProps = {
  platform: SocialImportPlatform
  open: boolean
  onOpenChange: (open: boolean) => void
  hasDocumentCapacity: boolean
  onImported?: (
    result: ImportKnowledgeBaseFromSocialResult,
  ) => void | Promise<void>
}

const INITIAL_VALUES: SocialImportFormValues = {
  profileUrl: "",
  maxPosts: KB_SOCIAL_IMPORT_DEFAULT_POSTS,
  category: "",
  tags: "",
  injectionMode: "auto",
  isDefault: false,
}

const INJECTION_MODE_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "direct", label: "Direct" },
  { value: "rag", label: "RAG" },
]

export function ImportFromSocialDialog({
  platform,
  open,
  onOpenChange,
  hasDocumentCapacity,
  onImported,
}: ImportFromSocialDialogProps) {
  const copy = getSocialImportDialogCopy(platform)
  const handleSuccess = async (result: ImportKnowledgeBaseFromSocialResult) => {
    onOpenChange(false)
    formik.resetForm()
    await onImported?.(result)
  }
  const mutation = useImportKnowledgeBaseFromSocialMutation({
    onSuccess: handleSuccess,
  })
  const formik = useFormik<SocialImportFormValues>({
    initialValues: INITIAL_VALUES,
    validate: values => validateSocialImport(values, platform),
    onSubmit: async (values, helpers) => {
      try {
        await mutation.mutateAsync({
          ...toSocialImportInput(values),
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
      } catch {
        // withQueryHandling shows the error and maps field validation errors.
      }
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen && !mutation.isPending) formik.resetForm()
        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-xl">
        <FormikProvider value={formik}>
          <form className="space-y-4" onSubmit={formik.handleSubmit}>
            <DialogHeader>
              <DialogTitle>{copy.title}</DialogTitle>
              <DialogDescription>
                {copy.description}{" "}
                <span>Video audio and text embedded in images are not transcribed.</span>
              </DialogDescription>
            </DialogHeader>

            <FormInput
              name="profileUrl"
              id={`${platform}-profile-url`}
              label={copy.urlLabel}
              placeholder={copy.placeholder}
            />

            {!hasDocumentCapacity ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Your plan has no knowledge-base document slots available.
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput name="category" label="Category" />
              <FormInput name="tags" label="Tags" placeholder={`${platform}, products`} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormSelect
                name="injectionMode"
                label="Mode"
                options={INJECTION_MODE_OPTIONS}
              />
              <MaxPostsField maxPostsLimit={getKbSocialImportMaxPosts(platform)} />
            </div>
            <ApplyToAllField />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  mutation.isPending ||
                  !hasDocumentCapacity ||
                  !formik.values.profileUrl.trim()
                }
              >
                {mutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {copy.buttonLabel}
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function MaxPostsField({ maxPostsLimit }: { maxPostsLimit: number }) {
  const { values, errors, touched, setFieldTouched, setFieldValue } =
    useFormikContext<SocialImportFormValues>()
  const error = touched.maxPosts ? errors.maxPosts : undefined
  return (
    <div className="space-y-1.5">
      <Label htmlFor="social-import-max-posts">Maximum recent posts</Label>
      <Input
        id="social-import-max-posts"
        type="number"
        min={1}
        max={maxPostsLimit}
        value={values.maxPosts}
        aria-invalid={Boolean(error)}
        onBlur={() => void setFieldTouched("maxPosts", true, true)}
        onChange={event => void setFieldValue("maxPosts", Number(event.target.value))}
      />
      {error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
    </div>
  )
}

function ApplyToAllField() {
  const { values, setFieldTouched, setFieldValue } =
    useFormikContext<SocialImportFormValues>()
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border p-3">
      <Checkbox
        checked={values.isDefault}
        onCheckedChange={checked => {
          void setFieldValue("isDefault", checked === true)
          void setFieldTouched("isDefault", true, false)
        }}
      />
      <span className="space-y-1">
        <span className="block text-sm font-medium">Apply to all agents</span>
        <span className="block text-xs text-muted-foreground">
          Make this imported profile default knowledge for current and new agents.
        </span>
      </span>
    </label>
  )
}

function validateSocialImport(
  values: SocialImportFormValues,
  platform: SocialImportPlatform,
) {
  const errors: Partial<Record<keyof SocialImportFormValues, string>> = {}
  const maxPostsLimit = getKbSocialImportMaxPosts(platform)
  if (!values.profileUrl.trim()) {
    errors.profileUrl = "Profile URL is required."
  } else {
    const parsedUrl = SocialProfileUrlSchema.safeParse(values.profileUrl)
    if (!parsedUrl.success || getSocialPlatform(parsedUrl.data) !== platform) {
      errors.profileUrl = getSocialImportDialogCopy(platform).validationMessage
    }
  }
  if (
    !Number.isInteger(values.maxPosts) ||
    values.maxPosts < 1 ||
    values.maxPosts > maxPostsLimit
  ) {
    errors.maxPosts = `Choose between 1 and ${maxPostsLimit} posts.`
  }
  return errors
}

function getSocialImportDialogCopy(platform: SocialImportPlatform): {
  title: string
  description: string
  urlLabel: string
  placeholder: string
  buttonLabel: string
  validationMessage: string
} {
  switch (platform) {
    case "instagram":
      return {
        title: "Import from Instagram",
        description: "Turn public Instagram profile details and post captions into one knowledge-base document.",
        urlLabel: "Instagram profile URL",
        placeholder: "https://www.instagram.com/your-business/",
        buttonLabel: "Import Instagram",
        validationMessage: "Enter a public Instagram profile URL.",
      }
    case "facebook":
      return {
        title: "Import from Facebook",
        description: "Turn public Facebook Page details and post captions into one knowledge-base document.",
        urlLabel: "Facebook Page URL",
        placeholder: "https://www.facebook.com/your-business/",
        buttonLabel: "Import Facebook",
        validationMessage: "Enter a public Facebook Page or profile URL.",
      }
    default: {
      const exhaustivePlatform: never = platform
      return exhaustivePlatform
    }
  }
}

function toSocialImportInput(
  values: SocialImportFormValues,
): ImportKnowledgeBaseFromSocialInput {
  return {
    profileUrl: values.profileUrl,
    maxPosts: values.maxPosts,
    category: values.category || undefined,
    tags: values.tags.split(",").map(tag => tag.trim()).filter(Boolean),
    injectionMode: values.injectionMode,
    isDefault: values.isDefault,
  }
}
